import { ReactElement, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TextField } from '../../../components/TextField';
import { Button } from '../../../components/Button/Button';
import { FocusPageLayout } from '../../../components/FocusPageLayout';
import { Paragraph } from '../../../components/Paragraph/Paragraph';
import { Routes } from '../../../enums/Routes';
import { useGetTenantsQuery } from '../../../hooks/queries/useGetTenantsQuery';
import { useUpdateTenantConfigMutation } from '../../../hooks/mutations/useUpdateTenantConfigMutation';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { Alert } from '../../../components/Alert/Alert';
import classes from './EditSeatsPage.module.css';

const PAGE_TITLE = 'Endre antall lisenser';
const SEATS_LABEL = 'Antall lisenser';
const SEATS_PLACEHOLDER = 'Antall lisenser, eller la stå tom for ubegrenset';
const SAVE_BUTTON_TEXT = 'Lagre endringer';
const UNLIMITED_TEXT = 'La feltet stå tomt for ubegrensede lisenser';
const ERROR_MESSAGE = 'Kunne ikke laste kundedata. Prøv igjen senere.';
const NOT_FOUND_MESSAGE = 'Kunde ikke funnet.';
const BACK_LINK_LABEL = 'Tilbake til kundedetaljer';

export const EditSeatsPage = (): ReactElement => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetTenantsQuery();
  const updateTenantConfigMutation = useUpdateTenantConfigMutation();

  const tenant = data?.tenants.find((t) => t.id === Number(tenantId));

  const [seats, setSeats] = useState<string>('');

  useEffect(() => {
    if (tenant) {
      setSeats(
        tenant.availableSeats && tenant.availableSeats > 0
          ? tenant.availableSeats.toString()
          : ''
      );
    }
  }, [tenant]);

  const backLink = Routes.SystemAdminTenantDetail.replace(':tenantId', tenantId || '');

  const handleSave = () => {
    if (!tenantId) return;

    const seatsValue = seats.trim() === '' ? null : Number(seats);

    updateTenantConfigMutation.mutate(
      {
        tenantId: Number(tenantId),
        payload: {
          availableSeats: seatsValue,
        },
      },
      {
        onSuccess: () => {
          toast.success('Antall lisenser oppdatert');
          navigate(backLink);
        },
        onError: () => {
          toast.error('Kunne ikke oppdatere antall lisenser. Prøv igjen senere.');
        },
      }
    );
  };

  const handleSeatsChange = (value: string) => {
    if (value === '' || /^\d+$/.test(value)) {
      setSeats(value);
    }
  };

  if (isLoading) {
    return <GhostLoader />;
  }

  if (error) {
    return (
      <Alert variant="danger" size="md">
        <Paragraph>{ERROR_MESSAGE}</Paragraph>
      </Alert>
    );
  }

  if (!tenant) {
    return (
      <Alert variant="warning" size="md">
        <Paragraph>{NOT_FOUND_MESSAGE}</Paragraph>
      </Alert>
    );
  }

  return (
    <FocusPageLayout
      title={PAGE_TITLE}
      subtitle={`Oppdater antall lisenser for ${tenant.name}`}
      backLink={backLink}
      backLinkLabel={BACK_LINK_LABEL}
    >
      <div className={classes.form}>
        <div className={classes.section}>
          <TextField
            label={SEATS_LABEL}
            type="text"
            name="seats"
            inputMode="numeric"
            value={seats}
            onChange={(e) => handleSeatsChange(e.target.value)}
            placeholder={SEATS_PLACEHOLDER}
          />
          <Paragraph className={classes.helperText}>
            {UNLIMITED_TEXT}
          </Paragraph>
        </div>

        <div className={classes.actions}>
          <Button
            variant="secondary"
            onClick={() => navigate(backLink)}
            disabled={updateTenantConfigMutation.isPending}
          >
            Avbryt
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={updateTenantConfigMutation.isPending}
          >
            {updateTenantConfigMutation.isPending ? 'Lagrer...' : SAVE_BUTTON_TEXT}
          </Button>
        </div>
      </div>
    </FocusPageLayout>
  );
};
